// beforeunload guard for the talent flow (Generation + UploadSummary).
// Browsers only show their generic "Leave site?" dialog — custom text is
// ignored — so the reminder is split in two: the dialog itself, and, if the
// talent chose "stay", a short pulse on every not-yet-uploaded asset in the
// sidebar. "Stayed" is inferred: beforeunload fired and the page is still
// alive one second later (a page that actually unloads never reaches the
// timer). GitHub #1, point 2. Pulses three times (Philip, 2026-09-14).

import { useEffect } from 'react';
import { buildAssetList } from '@/lib/assetList';
import { shouldWarnBeforeLeave } from '@/lib/leaveGuard';
import { useSessionStore } from '@/stores/session';

const STAYED_AFTER_MS = 1000;
// 3 Pulse × 0.8 s (AssetNav.module.css .remind) + Reserve.
export const UPLOAD_REMINDER_MS = 2600;

export function useLeaveGuard(): void {
  const configAssets = useSessionStore((s) => s.config?.assets);
  const configVoices = useSessionStore((s) => s.config?.voices);
  const customAssets = useSessionStore((s) => s.customAssets);
  const versions = useSessionStore((s) => s.versions);
  // Läuft ein Upload (Spinner), warnt der Guard auch ab v2 (Audit M15).
  const assetBusy = useSessionStore((s) => s.assetBusy);
  const setUploadReminder = useSessionStore((s) => s.setUploadReminder);

  const shouldWarn = shouldWarnBeforeLeave({
    assets: buildAssetList(configAssets, configVoices, customAssets),
    versions,
    assetBusy,
  });

  useEffect(() => {
    if (!shouldWarn) return;
    let stayTimer: number | undefined;
    let clearTimer: number | undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy Chromium still needs returnValue for the dialog to appear.
      e.returnValue = '';
      window.clearTimeout(stayTimer);
      window.clearTimeout(clearTimer);
      stayTimer = window.setTimeout(() => {
        setUploadReminder(true);
        clearTimer = window.setTimeout(() => setUploadReminder(false), UPLOAD_REMINDER_MS);
      }, STAYED_AFTER_MS);
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.clearTimeout(stayTimer);
      window.clearTimeout(clearTimer);
      setUploadReminder(false);
    };
  }, [shouldWarn, setUploadReminder]);
}
